import React, { useState, createContext, useContext, useEffect } from "react";
import { useRouter } from "next/router";
import { useQuickActions } from "./quick-actions-provider";
import { useAxios, handleError, setWindowVariables, translateUpgradeProfileProducts } from "../utils/helpers";
import {
    DB_SUBSCRIPTION_STATUS_CANCELLED,
    DB_SUBSCRIPTION_STATUS_PAUSED,
    DB_SUBSCRIPTION_STATUS_EXPIRED,
    DB_ORDER_STATUS_PROCESSING,
    SUBSCRIPTION_STATUS_READABLE,
} from "../utils/constants";

import { Loader, useToast } from "../design";
import { addDays, addMonths, addWeeks, addYears, fromUnixTime } from "date-fns";
import { tryCatch } from "../utils/try-catch";
import Translate from "../utils/translations";
import { CustomerPortalContext } from "../backend/serverProps";
import useNavigationURL from "../hooks/useNavigationURL";

const ContractContext = createContext(null);
let applicationInitialized = false;

const getNextOrderDate = (
    billing_date: any,
    chargeOffset: any,
    interval?: "DAY" | "WEEK" | "MONTH" | "YEAR",
    intervalCount?: number,
) => {
    let baseDate = fromUnixTime(billing_date);
    if (chargeOffset) {
        baseDate = addDays(baseDate, chargeOffset);
    }
    if (interval && intervalCount) {
        switch (interval) {
            case "DAY":
                baseDate = addDays(baseDate, intervalCount);
                break;
            case "WEEK":
                baseDate = addWeeks(baseDate, intervalCount);
                break;
            case "MONTH":
                baseDate = addMonths(baseDate, intervalCount);
                break;
            case "YEAR":
                baseDate = addYears(baseDate, intervalCount);
                break;
            default:
                break;
        }
    }

    return Math.ceil(baseDate.getTime() / 1000);
};

function _getContractProperties(newData: any) {
    try {
        let basePriceTotal: number = 0;
        let totalLineItemPrice: number = 0;
        let diff: number = 0;
        if (newData?.line_items) {
            newData?.line_items.forEach((lineItem: any) => {
                const isBundle: boolean = lineItem.isBundleItems;
                if (isBundle) {
                    lineItem.bundleItems.forEach((bundleItem: any) => {
                        basePriceTotal += bundleItem.base_price;
                        totalLineItemPrice += parseFloat(bundleItem.price);
                    });
                } else {
                    basePriceTotal += lineItem.base_price;
                    totalLineItemPrice += parseFloat(lineItem.price);
                }
            });

            diff = basePriceTotal - totalLineItemPrice;
            if (diff <= 0) {
                diff = 0;
            }
        }

        return {
            id: newData.id,
            shopifyId: newData.shopify_id,
            status: SUBSCRIPTION_STATUS_READABLE[newData.status],
            hasBundle: newData.subscription_has_bundle,
            totalLineItemDiscountedPrice: newData.total_line_item_discounted_price,
            deliveryPrice: newData.delivery_price,
            currencyCode: newData.currency_code,
            completedOrdersCount: newData.billing_attempt_success_count,
            nextBillingDateEpoch: newData.next_billing_date,
            nextOrderDateAfterSkipEpoch: getNextOrderDate(
                newData.next_billing_date,
                newData.chargeOffset,
                newData?.delivery_policy?.interval,
                newData?.delivery_policy?.interval_count,
            ),
            subscriptionDiscount: parseFloat(diff.toFixed(2)),
            specificSubscriptionsSavingsNextOrder: parseFloat(diff.toFixed(2)),
            upcomingOrderId: newData.upcoming_order?.id,
            createdAt: newData.createdAt,
            billingPolicy: {
                maxCycles: newData?.billing_policy?.max_cycles,
                minCycles: newData?.billing_policy?.min_cycles,
                interval: newData?.billing_policy?.interval,
                intervalCount: newData?.billing_policy?.interval_count,
                anchorDay: newData?.billing_policy?.anchor_day,
                anchorMonth: newData?.billing_policy?.anchor_month,
                anchorType: newData?.billing_policy?.anchor_type,
                prepaidIntervalCount: newData?.billing_policy?.prepaid_interval_count,
            },
            deliveryPolicy: {
                interval: newData?.delivery_policy?.interval,
                intervalCount: newData?.delivery_policy?.interval_count,
                anchorDay: newData?.delivery_policy?.anchor_day,
                anchorMonth: newData?.delivery_policy?.anchor_month,
                anchorType: newData?.delivery_policy?.anchor_type,
                prepaidIntervalCount: newData?.delivery_policy?.prepaidIntervalCount,
            },
            subscriptionAttributes: newData?.custom_attributes,
            autoResume: newData?.autoResume,
            // customerPaymentMethodId: newData?.customerPaymentMethod_id,
            discounts: newData?.discounts,
            note: newData?.note,
            chargeOffset: newData?.chargeOffset,
            totalLineItemPrice: newData?.total_line_item_price,
            // domain: newData?.domain,
            isPrepaid: newData?.is_prepaid ? true : false,
            deliveryMethod: newData?.deliveryMethod,
            shippingAddress: newData?.shipping_address,
            availableDeliveryIntervals: newData?.available_delivery_intervals,
            totalSpecificSubscriptionSpentByCustomer: newData?.total_specific_subscription_spent_by_customer,
            lastSuccessfulOrder: newData?.lastSuccessfulOrder,
            isPrepaidV2: newData?.isPrepaidV2,
            isMarkedForCancellation: newData?.isMarkedForCancellation ? true : false,
            // storeDefaultLocale: newData?.storeDefaultLocale,
            // paymentErrorMessage: newData?.paymentErrorMessage,
            paymentErrorMessageDisplay: newData?.paymentErrorMessageDisplay,
            deliveryPriceCurrencyCode: newData?.delivery_price_currency_code,
            remainingPrepaidOrdersCount:
                newData?.isPrepaidV2 &&
                    newData?.billing_policy?.prepaid_interval_count &&
                    newData?.billing_attempt_success_count
                    ? newData?.billing_policy?.prepaid_interval_count -
                    (newData?.billing_policy?.prepaid_interval_count % newData?.billing_attempt_success_count)
                    : null,
            isPrepaidAutoRenewalEnabled: newData?.isPrepaidV2 && !newData?.billing_policy?.max_cycles ? true : false,
            // upcomingOrder: {
            //     id: newData?.upcoming_order.id,
            //     shopify_id: newData?.upcoming_order.shopify_id,
            //     domain: newData?.upcoming_order.domain,
            //     shopify_subscriptionContract_id: newData?.upcoming_order.shopify_subscriptionContract_id,
            //     available_retry_count: newData?.upcoming_order.available_retry_count,
            //     admin_graphql_api_subscriptionContract_id: newData?.upcoming_order.admin_graphql_api_subscriptionContract_id,
            //     admin_graphql_api_id: newData?.upcoming_order.admin_graphql_api_id,
            //     phone: newData?.upcoming_order.phone,
            //     shopify_order_number: newData?.upcoming_order.shopify_order_number,
            //     contact_email: newData?.upcoming_order.contact_email,
            //     shopify_customer_id: newData?.upcoming_order.shopify_customer_id,
            //     future_order_status: newData?.upcoming_order.future_order_status,
            //     subscriptionContract_id: newData?.upcoming_order.subscriptionContract_id,
            //     shopify_app_id: newData?.upcoming_order.shopify_app_id,
            //     total_price: newData?.upcoming_order.total_price,
            //     billing_date: newData?.upcoming_order.billing_date,
            //     reminder_sent: newData?.upcoming_order.reminder_sent,
            //     subtotal_price: newData?.upcoming_order.subtotal_price,
            //     total_tax: newData?.upcoming_order.total_tax,
            //     currency: newData?.upcoming_order.currency,
            //     total_price_usd: newData?.upcoming_order.total_price_usd,
            //     total_discount: newData?.upcoming_order.total_discount,
            //     total_line_items_price: newData?.upcoming_order.total_line_items_price,
            //     total_shipping_price: newData?.upcoming_order.total_shipping_price,
            //     total_shipping_price_currency: newData?.upcoming_order.total_shipping_price_currency,
            //     idempotency_key: newData?.upcoming_order.idempotency_key,
            //     is_origin_order: newData?.upcoming_order.is_origin_order ? true : false,
            //     shipping_lines: newData?.upcoming_order.shipping_lines,
            //     shipping_address: newData?.upcoming_order.shipping_address,
            //     billing_address: newData?.upcoming_order.billing_address,
            //     tax_lines: newData?.upcoming_order.tax_lines,
            //     shopify_created_at: newData?.upcoming_order.shopify_created_at,
            //     shopify_processed_at: newData?.upcoming_order.shopify_processed_at,
            //     shopify_updated_at: newData?.upcoming_order.shopify_updated_at,
            //     discount_codes: newData?.upcoming_order.discount_codes,
            //     note: newData?.upcoming_order.note,
            //     note_attributes: newData?.upcoming_order.note_attributes,
            //     cart_token: newData?.upcoming_order.cart_token,
            //     tags: newData?.upcoming_order.tags,
            //     total_weight: newData?.upcoming_order.total_weight,
            //     is_rescheduled: newData?.upcoming_order.is_rescheduled ? true : false,
            //     fulfillments: newData?.upcoming_order.fulfillments,
            //     refunds: newData?.upcoming_order.refunds,
            //     fulfillment_status: newData?.upcoming_order.fulfillment_status,
            //     financial_status: newData?.upcoming_order.financial_status,
            //     current_total_price_set: newData?.upcoming_order.current_total_price_set,
            //     current_subtotal_price_set: newData?.upcoming_order.current_subtotal_price_set,
            //     current_total_discounts_set: newData?.upcoming_order.current_total_discounts_set,
            //     current_total_duties_set: newData?.upcoming_order.current_total_duties_set,
            //     current_total_tax_set: newData?.upcoming_order.current_total_tax_set,
            //     subtotal_price_set: newData?.upcoming_order.subtotal_price_set,
            //     total_discounts_set: newData?.upcoming_order.total_discounts_set,
            //     total_line_items_price_set: newData?.upcoming_order.total_line_items_price_set,
            //     total_price_set: newData?.upcoming_order.total_price_set,
            //     total_shipping_price_set: newData?.upcoming_order.total_shipping_price_set,
            //     processed_at_loop: newData?.upcoming_order.processed_at_loop,
            //     is_challenged: newData?.upcoming_order.is_challenged ? true : false,
            //     next_action_url: newData?.upcoming_order.next_action_url,
            //     total_tax_set: newData?.upcoming_order.total_tax_set,
            //     billing_attempt_graphql_id: newData?.upcoming_order.billing_attempt_graphql_id,
            //     isInventoryDelay: newData?.upcoming_order.isInventoryDelay ? true : false,
            //     payment_gateway_names: newData?.upcoming_order.payment_gateway_names,
            //     originTime: newData?.upcoming_order.originTime,
            //     chargeOffset: newData?.upcoming_order.chargeOffset,
            //     createdAt: newData?.upcoming_order.createdAt,
            //     updatedAt: newData?.upcoming_order.updatedAt,
            // },
            paymentMethod: {
                // id: newData?.payment_method.id,
                // admin_graphql_api_id: newData?.payment_method?.admin_graphql_api_id,
                // customer_id: newData?.payment_method.customer_id,
                // shopify_customer_id: newData?.payment_method.shopify_customer_id,
                // instrument_type: newData?.payment_method.instrument_type,
                payment_instrument: newData?.payment_method?.payment_instrument,
                is_revoked: newData?.payment_method?.is_revoked ? true : false,
                risk_level: newData?.payment_method?.risk_level,
                update_mail_sent_at: newData?.payment_method?.update_mail_sent_at,
                expiring_30days_event_emitted_at: newData?.payment_method?.expiring_30days_event_emitted_at,
                expiring_15days_event_emitted_at: newData?.payment_method?.expiring_15days_event_emitted_at,
                expiring_3days_event_emitted_at: newData?.payment_method?.expiring_3days_event_emitted_at,
                paymentMethodLastUpdatedAt: newData?.payment_method?.paymentMethodLastUpdatedAt,
                // createdAt: newData?.payment_method?.createdAt,
                // updatedAt: newData?.payment_method?.updatedAt,
            },
            lastBillingAttempt: {
                // id: newData?.last_billing_attempt.id,
                // shopify_id: newData?.last_billing_attempt.shopify_id,
                // order_id: newData?.last_billing_attempt.order_id,
                // idempotency_key: newData?.last_billing_attempt.idempotency_key,
                // shopify_subscriptionContract_id: newData?.last_billing_attempt.shopify_subscriptionContract_id,
                shopify_order_id: newData?.last_billing_attempt?.shopify_order_id,
                // ready: newData?.last_billing_attempt.ready,
                error_message: newData?.last_billing_attempt?.error_message,
                // error_code: newData?.last_billing_attempt.error_code,
                completed_at: newData?.last_billing_attempt?.completed_at,
                next_action_url: newData?.last_billing_attempt?.next_action_url,
                // createdAt: newData?.last_billing_attempt.createdAt,
                // updatedAt: newData?.last_billing_attempt.updatedAt,
            },
            lineItems: (newData?.line_items || []).map((lineItem: any) => ({
                id: lineItem.id,
                title: lineItem.title,
                variantTitle: lineItem.variant_title,
                price: lineItem.price,
                quantity: lineItem.quantity,
                variantId: lineItem.variant_id,
                productId: lineItem.product_id,
                // pricingPolicy: lineItem.pricing_policy,
                discountAllocations: lineItem.discount_allocations,
                basePrice: lineItem.base_price,
                isOneTimeAdded: lineItem.is_one_time_added ? true : false,
                isOneTimeRemoved: lineItem.is_one_time_removed ? true : false,
                discountedPrice: lineItem.discounted_price,
                bundleTransactionId: lineItem.bundleTransactionId,
                // currencyCode: lineItem.currency_code,
                sellingPlanName: lineItem.selling_plan_name,
                sellingPlanId: lineItem.selling_plan_id,
                sellingPlanGroupId: lineItem.selling_plan_group_id,
                sellingPlanGroupName: lineItem.selling_plan_group_name,
                sku: lineItem.sku,
                requiresShipping: lineItem.requires_shipping,
                originalDiscountedPrice: lineItem.originalDiscountedPrice,
                variantImage: lineItem.variant_image,
                // inventoryPolicy: lineItem.inventory_policy,
                // inventoryManagement: lineItem.inventory_management,
                inventoryQuantity: lineItem.inventory_quantity,
                productSku: lineItem.productSku,
                productHandle: lineItem.product_handle,
                canSwap: lineItem.can_swap,
                loopBundleId: lineItem.loopBundleId,
                bundleName: lineItem.name,
                isPresetBundle: lineItem.isPresetBundle,
                isBundleItems: lineItem.isBundleItems,
                bundleItems: lineItem.bundleItems,
                customAttributes: lineItem.customAttributes,
            })),
        };
    } catch (error) {
        handleError(error, "contract-provider.tsx", "_getContractProperties", newData);
        return {};
    }
}

export function ContractProvider(props: {
    children: React.ReactNode;
    customerPortalContext: CustomerPortalContext | null;
}) {
    const router = useRouter();
    const [axios] = useAxios();
    const toasts = useToast();
    const { loadQuickAction } = useQuickActions();
    const navigationURL = useNavigationURL();

    const [isLoading, setisLoading] = useState(true);
    const [contextValue, setcontextValue]: Array<any> = useState({});

    // Extract contract data from props if available
    const serverSideData = props.customerPortalContext;

    const displayActionMessage = async (message: string, success: boolean) => {
        if (success) {
            await loadQuickAction(false);
            await loadContract();
        }
        toasts.open({
            message,
            time: 10000,
            type: success ? "success" : "failure",
        });
    };

    const displayActionMessageNoReload = async (message: string, success: boolean) => {
        toasts.open({
            message,
            time: 10000,
            type: success ? "success" : "failure",
        });
    };

    const getStreakRewardsAndTexts = (_rewards) => {
        const rewards = JSON.parse(JSON.stringify(_rewards));
        const translations = Translate.fetch();
        return rewards.map((reward) => {
            const streakKey = "miniStreakRewardTitle";
            const streakDescriptionKey = "miniStreakRewardDescription";
            const streakProgressBarImgUrlKey = "miniStreakRewardProgressBarImgUrl";
            const streakProgressBarImgUrlMobileKey = "miniStreakRewardProgressBarImgUrlMobile";
            const obj = {
                [streakKey]: translations[`MINI_STREAK_${reward.id}_${streakKey}`],
                [streakDescriptionKey]: translations[`MINI_STREAK_${reward.id}_${streakDescriptionKey}`],
                [streakProgressBarImgUrlKey]: translations[`MINI_STREAK_${reward.id}_${streakProgressBarImgUrlKey}`],
                [streakProgressBarImgUrlMobileKey]:
                    translations[`MINI_STREAK_${reward.id}_${streakProgressBarImgUrlMobileKey}`],
            };
            reward.texts = obj;
            delete reward.id;
            delete reward.miniStreakId;
            return reward;
        });
    };

    const createStreakWindowData = (streakData) => {
        if (streakData) {
            return {
                programName: streakData.miniStreakTitle,
                streakLength: streakData.requiredCount,
                gracePeriod: streakData.graceDays,
                status: streakData.status,
                rewards: getStreakRewardsAndTexts(streakData.rewards),
            };
        }
        return null;
    };

    const loadContract = async () => {
        setisLoading(true);
        try {
            const Shopify = (window as any).Shopify;
            const myShopifyDomain = Shopify?.shop ?? process.env.NEXT_PUBLIC_DEV_MYSHOPIFY_DOMAIN;
            const _locale = Shopify?.locale || router?.query?.locale || "en";

            // Use server-side data if available
            if (serverSideData && applicationInitialized === false) {
                const contractData = serverSideData.contract;
                const deliveryMethodData = serverSideData.deliveryMethod;
                let isZapietIntegrated = false;
                if (serverSideData.integrations && Object.hasOwn(serverSideData.integrations, "zapiet")) {
                    isZapietIntegrated = true;
                }
                const reasons = serverSideData.cancellationReasons;

                const data = contractData;

                if (!data) {
                    router.replace(`${process.env.NEXT_PUBLIC_PROXY_PATH}/get-subscription-link`);
                    setisLoading(false);
                    return;
                }

                let cancelledPausedOrExpired = false;
                let cancelledOrPaused = false;
                if (
                    data?.status == DB_SUBSCRIPTION_STATUS_CANCELLED ||
                    data?.status == DB_SUBSCRIPTION_STATUS_PAUSED ||
                    data?.status == DB_SUBSCRIPTION_STATUS_EXPIRED
                ) {
                    cancelledPausedOrExpired = true;
                }
                if (data?.status == DB_SUBSCRIPTION_STATUS_CANCELLED || data?.status == DB_SUBSCRIPTION_STATUS_PAUSED) {
                    cancelledOrPaused = true;
                }

                const _detailsBody = {
                    subscriptionContractShopifyId: Number(router.query.subscription_shopify_id),
                    source: "CUSTOMER_PORTAL",
                };

                const [upgradeProfilesResult, billingDetailsResult, contractStreaksResult] = await Promise.all([
                    tryCatch(
                        axios.get(
                            `${process.env.NEXT_PUBLIC_API_SERVICE}/subscriptionUpgrade/profile/cp/assess?subscriptionContractShopifyId=${router.query.subscription_shopify_id}&locale=${_locale}`,
                        ),
                    ),
                    tryCatch(
                        axios.post(`${process.env.NEXT_PUBLIC_API_SERVICE}/paymentFlow/customer/details`, _detailsBody),
                    ),
                    tryCatch(
                        axios.get(
                            `${process.env.NEXT_PUBLIC_API_SERVICE}/miniStreak/assess?subscriptionContractShopifyId=${router.query.subscription_shopify_id}`,
                        ),
                    ),
                ]);

                const upgradeProfilesRes = upgradeProfilesResult.data;
                const billingDetailsAndPreferences = billingDetailsResult.data;
                const contractStreaksData = contractStreaksResult.data;

                if (upgradeProfilesResult?.error)
                    handleError(
                        upgradeProfilesResult?.error,
                        "components/contract-provider",
                        "loadData - upgradeProfiles",
                        {},
                    );
                if (billingDetailsResult?.error)
                    handleError(
                        billingDetailsResult?.error,
                        "components/contract-provider",
                        "loadData - billingDetails",
                        {},
                    );
                if (contractStreaksResult?.error)
                    handleError(
                        contractStreaksResult?.error,
                        "components/contract-provider",
                        "loadData - contractStreaks",
                        {},
                    );

                const windowVariables = _getContractProperties(JSON.parse(JSON.stringify(data?.data ?? {})));
                setWindowVariables({
                    subscription: {
                        ...windowVariables,
                        streak: createStreakWindowData(contractStreaksData?.data?.data?.assessment),
                    },
                    allSubscriptions: null,
                    upgradeProfiles: upgradeProfilesRes?.data?.eligibleProfiles
                        ? upgradeProfilesRes?.data?.eligibleProfiles.map((profile: any) => {
                            return {
                                profileId: profile?.id,
                                profileName: profile?.name,
                                priority: profile?.priority,
                            };
                        })
                        : [],
                });

                setcontextValue({
                    contract: data,
                    subscriptionDiscount: windowVariables?.subscriptionDiscount,
                    specificSubscriptionsSavingsNextOrder: windowVariables?.specificSubscriptionsSavingsNextOrder,
                    displayActionMessage: displayActionMessage,
                    displayActionMessageNoReload: displayActionMessageNoReload,
                    cancelledPausedOrExpired: cancelledPausedOrExpired,
                    cancelledOrPaused: cancelledOrPaused,
                    expired: data?.status == DB_SUBSCRIPTION_STATUS_EXPIRED || false,
                    cancellation_reasons: reasons,
                    isOrderProcessing: data?.upcoming_order?.future_order_status == DB_ORDER_STATUS_PROCESSING || false,
                    contractDeliveryMethod: deliveryMethodData,
                    storeDefaultLocale: data?.storeDefaultLocale,
                    zapietIntegrated: isZapietIntegrated,
                    upcomingOrderId: windowVariables?.upcomingOrderId,
                    billingDetailsAndPreferences: billingDetailsAndPreferences
                        ? billingDetailsAndPreferences?.data?.data
                        : null,
                    eligibleUpgradeProfiles: await translateUpgradeProfileProducts(
                        upgradeProfilesRes?.data?.eligibleProfiles ?? [],
                        _locale,
                        data?.storeDefaultLocale,
                        myShopifyDomain,
                    ),
                    upgradeProfileDisplayType: upgradeProfilesRes?.data?.displayType ?? "BUTTON",
                    contractStreaksData: contractStreaksData?.data?.data,
                });
                setisLoading(false);
                applicationInitialized = true;
                return;
            }

            // Fallback to client-side data fetching
            const _detailsBody = {
                subscriptionContractShopifyId: Number(router.query.subscription_shopify_id),
                source: "CUSTOMER_PORTAL",
            };
            const [
                contractResult,
                deliveryMethodResult,
                zapietResult,
                cancellationReasonsResult,
                upgradeProfilesResult,
                billingDetailsResult,
                contractStreaksResult,
            ] = await Promise.all([
                tryCatch(
                    axios.get(
                        `${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/customer/v2/read_contract?` +
                        new URLSearchParams({
                            subscription_shopify_id: router.query.subscription_shopify_id + "",
                        }),
                    ),
                ),
                tryCatch(
                    axios.get(
                        `${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/customer/v2/deliveryMethod?` +
                        new URLSearchParams({
                            shopifyId: router.query.subscription_shopify_id + "",
                        }),
                    ),
                ),
                tryCatch(
                    axios.get(`${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/customer/v2/checkIfZapietIsIntegrated`),
                ),
                tryCatch(
                    axios.get(
                        `${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/customer/v2/readCancellationReasons?` +
                        new URLSearchParams({
                            subscriptionShopifyId: router.query.subscription_shopify_id + "",
                        }),
                    ),
                ),
                tryCatch(
                    axios.get(
                        `${process.env.NEXT_PUBLIC_API_SERVICE}/subscriptionUpgrade/profile/cp/assess?subscriptionContractShopifyId=${router.query.subscription_shopify_id}&locale=${_locale}`,
                    ),
                ),
                tryCatch(
                    axios.post(`${process.env.NEXT_PUBLIC_API_SERVICE}/paymentFlow/customer/details`, _detailsBody),
                ),
                tryCatch(
                    axios.get(
                        `${process.env.NEXT_PUBLIC_API_SERVICE}/miniStreak/assess?subscriptionContractShopifyId=${router.query.subscription_shopify_id}`,
                    ),
                ),
            ]);

            const contractData = contractResult.data;
            const _deliveryMethodData = deliveryMethodResult.data;
            const _zapietIntegrated = zapietResult.data;
            const reasons = cancellationReasonsResult.data;
            const upgradeProfilesRes = upgradeProfilesResult.data;
            const billingDetailsAndPreferences = billingDetailsResult.data;
            const contractStreaksData = contractStreaksResult.data;

            if (contractResult?.error)
                handleError(contractResult?.error, "components/contract-provider", "loadData - contract", {});
            if (deliveryMethodResult?.error)
                handleError(
                    deliveryMethodResult?.error,
                    "components/contract-provider",
                    "loadData - deliveryMethod",
                    {},
                );
            if (zapietResult?.error)
                handleError(zapietResult?.error, "components/contract-provider", "loadData - zapiet", {});
            if (cancellationReasonsResult?.error)
                handleError(
                    cancellationReasonsResult?.error,
                    "components/contract-provider",
                    "loadData - cancellationReasons",
                    {},
                );
            if (upgradeProfilesResult?.error)
                handleError(
                    upgradeProfilesResult?.error,
                    "components/contract-provider",
                    "loadData - upgradeProfiles",
                    {},
                );
            if (billingDetailsResult?.error)
                handleError(
                    billingDetailsResult?.error,
                    "components/contract-provider",
                    "loadData - billingDetails",
                    {},
                );
            if (contractStreaksResult?.error)
                handleError(
                    contractStreaksResult?.error,
                    "components/contract-provider",
                    "loadData - contractStreaks",
                    {},
                );

            const data = contractData?.data;

            if (!data) {
                router.replace(`${process.env.NEXT_PUBLIC_PROXY_PATH}/get-subscription-link`);
                setisLoading(false);
                return;
            }

            let cancelledPausedOrExpired = false;
            let cancelledOrPaused = false;
            if (
                data?.data?.status == DB_SUBSCRIPTION_STATUS_CANCELLED ||
                data?.data?.status == DB_SUBSCRIPTION_STATUS_PAUSED ||
                data?.data?.status == DB_SUBSCRIPTION_STATUS_EXPIRED
            ) {
                cancelledPausedOrExpired = true;
            }
            if (
                data?.data?.status == DB_SUBSCRIPTION_STATUS_CANCELLED ||
                data?.data?.status == DB_SUBSCRIPTION_STATUS_PAUSED
            ) {
                cancelledOrPaused = true;
            }

            const windowVariables = _getContractProperties(JSON.parse(JSON.stringify(data?.data ?? {})));
            setWindowVariables({
                subscription: {
                    ...windowVariables,
                    streak: createStreakWindowData(contractStreaksData?.data?.data?.assessment),
                },
                allSubscriptions: null,
                upgradeProfiles: upgradeProfilesRes?.data?.eligibleProfiles
                    ? upgradeProfilesRes?.data?.eligibleProfiles.map((profile: any) => {
                        return {
                            profileId: profile?.id,
                            profileName: profile?.name,
                            priority: profile?.priority,
                        };
                    })
                    : [],
            });

            setcontextValue({
                contract: data?.data,
                subscriptionDiscount: windowVariables?.subscriptionDiscount,
                specificSubscriptionsSavingsNextOrder: windowVariables?.specificSubscriptionsSavingsNextOrder,
                displayActionMessage: displayActionMessage,
                displayActionMessageNoReload: displayActionMessageNoReload,
                cancelledPausedOrExpired: cancelledPausedOrExpired,
                cancelledOrPaused: cancelledOrPaused,
                expired: data?.data?.status == DB_SUBSCRIPTION_STATUS_EXPIRED || false,
                cancellation_reasons: reasons?.data?.data,
                isOrderProcessing:
                    data?.data?.upcoming_order?.future_order_status == DB_ORDER_STATUS_PROCESSING || false,
                contractDeliveryMethod: _deliveryMethodData?.data?.data,
                storeDefaultLocale: data?.data?.storeDefaultLocale,
                zapietIntegrated: _zapietIntegrated?.data?.data?.integrationActive,
                upcomingOrderId: windowVariables?.upcomingOrderId,
                billingDetailsAndPreferences: billingDetailsAndPreferences
                    ? billingDetailsAndPreferences?.data?.data
                    : null,
                eligibleUpgradeProfiles: await translateUpgradeProfileProducts(
                    upgradeProfilesRes?.data?.eligibleProfiles ?? [],
                    _locale,
                    data?.data?.storeDefaultLocale,
                    myShopifyDomain,
                ),
                upgradeProfileDisplayType: upgradeProfilesRes?.data?.displayType ?? "BUTTON",
                contractStreaksData: contractStreaksData?.data?.data,
            });
        } catch (error) {
            handleError(error, "components/contract-provider", "loadData", {});
            router.replace(`${process.env.NEXT_PUBLIC_PROXY_PATH}/get-subscription-link`);
        }
        setisLoading(false);
    };

    const refreshContract = async () => {
        const _params: any = {
            subscription_shopify_id: router.query.subscription_shopify_id + "",
        };
        const { data }: any = await axios.get(
            `${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/customer/v2/read_contract?` + new URLSearchParams(_params),
        );

        const windowVariables = _getContractProperties(JSON.parse(JSON.stringify(data?.data ?? {})));
        setWindowVariables({
            subscription: {
                ...windowVariables,
                streak: createStreakWindowData(contextValue?.contractStreaksData?.assessment),
            },
            allSubscriptions: null,
        });
        setcontextValue({
            ...contextValue,
            contract: data?.data,
        });
    };

    useEffect(() => {
        if (router.isReady && router.query.subscription_shopify_id && router.query.customer_shopify_id) {
            loadContract();
        }
    }, [router]);

    return (
        <ContractContext.Provider
            value={{
                ...contextValue,
                loadContract: loadContract,
                refreshContract: refreshContract,
                setcontextValue: setcontextValue,
            }}
        >
            {isLoading || !contextValue?.contract ? (
                <div className="loop-background" id="loop-background">
                    {" "}
                    <Loader />{" "}
                </div>
            ) : (
                props.children
            )}
        </ContractContext.Provider>
    );
}

export const useContract = () => useContext(ContractContext);
